import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getArticleById, updateModerationArticle } from "../../HTTP/articles";
import { updateVisibleArticle } from "../../HTTP/admin";
import { FEED_REPORT_REASONS } from "../Common/feedReportReasons";

export default function CheckArticle() {
  const user = JSON.parse(localStorage.getItem("user"));
  if (!user?.admin) {
    window.location.href = "/";
  }

  const { id } = useParams();

  const [formData, setFormData] = useState({
    id: id,
    user_id: user?.id,
    title: "",
    description: "",
    visible: 0,
    featured_image: "",
  });

  // --- Refus: état du popup + champs ---
  const [isRefuseOpen, setIsRefuseOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const funArticles = async () => {
      const resArticle = await getArticleById(id);
      setFormData(resArticle);
    };
    funArticles();
  }, [id]);

  const handlePublishArticle = async () => {
    setSubmitting(true);
    setErrorMsg("");
    try {
      const res = await updateModerationArticle(id);
      if (res) {
        alert("Article accepté");
        window.location.href = "/admin/articles";
      } else {
        alert("Une erreur est survenue lors de l’acceptation de l’article");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- Modal helpers ---
  const openRefuseModal = () => {
    setSelectedReason("");
    setCustomReason("");
    setErrorMsg("");
    setIsRefuseOpen(true);
  };
  const closeRefuseModal = () => setIsRefuseOpen(false);

  // Focus / clavier (Esc) quand la modale est ouverte
  const modalRef = useRef(null);
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") closeRefuseModal();
    }
    if (isRefuseOpen) {
      document.addEventListener("keydown", onKeyDown);
      // focus la modale
      setTimeout(() => modalRef.current?.focus(), 0);
      // empêcher scroll du body
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [isRefuseOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) closeRefuseModal();
  };

  const handleConfirmRefuse = async () => {
    setErrorMsg("");

    if (!selectedReason) {
      setErrorMsg("Veuillez sélectionner un motif de refus.");
      return;
    }

    const isOther = selectedReason.startsWith("Autre");
    const reasonToSend = isOther ? customReason.trim() : selectedReason;

    if (isOther && reasonToSend.length === 0) {
      setErrorMsg("Veuillez préciser le motif dans le champ texte.");
      return;
    }

    setSubmitting(true);
    try {
      const res1 = await updateVisibleArticle(id, 0, reasonToSend);
      const res2 = await updateModerationArticle(id);
      const res = res1 && res2;

      if (res) {
        alert("Article refusé");
        window.location.href = "/admin/articles";
      } else {
        alert("Une erreur est survenue lors du refus de l’article");
      }
    } finally {
      setSubmitting(false);
      closeRefuseModal();
    }
  };

  if (formData.visible === 0 && user?.admin === 0 && user?.id !== formData.user_id) {
    window.location.href = "/articles";
  }

  return (
    <>
      <div
        className="mt-8 max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md md:overflow-y-auto"
        style={{ maxHeight: window.innerWidth >= 768 ? "calc(100vh - 100px)" : undefined }}
      >
        <h1 className="text-2xl font-semibold text-gray-800 text-center">Vérification de l'article</h1>

        {formData.featured_image && (
          <>
            <h2 className="text-xl font-semibold text-gray-800 mt-8 text-center">Image</h2>
            <img
              className="w-full h-auto max-h-[60vh] object-cover rounded"
              src={formData.featured_image}
              alt="Article"
            />
          </>
        )}

        <h2 className="text-xl font-semibold text-gray-800 mt-8 text-center">Titre de l'article</h2>
        <h3 className="text-lg font-semibold text-gray-800 mt-2 text-center notranslate">
          {formData.title}
        </h3>

        <h2 className="text-xl font-semibold text-gray-800 mt-8 text-center">Description de l'article</h2>
        <div
          className="max-w-2xl mx-auto mt-4 p-6 bg-white shadow-md rounded max-h-[60vh] overflow-y-auto notranslate"
          dangerouslySetInnerHTML={{ __html: formData.description }}
        />

        {/* Actions principales */}
        <div className="max-w-2xl mx-auto flex flex-col items-center">
          <div className="flex flex-col sm:flex-row sm:justify-center gap-3 mt-6">
            <button
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
              onClick={handlePublishArticle}
              disabled={submitting}
            >
              Accepter
            </button>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
              onClick={openRefuseModal}
              disabled={submitting}
            >
              Refuser
            </button>
          </div>
        </div>
      </div>

      {/* --- MODALE DE REFUS --- */}
      {isRefuseOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={handleBackdropClick}
          aria-modal="true"
          role="dialog"
          aria-labelledby="refuse-title"
          aria-describedby="refuse-desc"
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            className="w-full max-w-xl bg-white rounded-xl shadow-xl outline-none"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 id="refuse-title" className="text-lg font-semibold text-gray-800">
                Refuser l’article
              </h2>
              <button
                onClick={closeRefuseModal}
                className="p-2 rounded hover:bg-gray-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <p id="refuse-desc" className="text-sm text-gray-600">
                Sélectionnez un motif de refus. Si vous choisissez “Autre problème”, indiquez une
                courte explication.
              </p>

              <fieldset className="space-y-3">
                {FEED_REPORT_REASONS.map(({ label }) => (
                  <label key={label} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="refusalReason"
                      value={label}
                      checked={selectedReason === label}
                      onChange={(e) => {
                        setSelectedReason(e.target.value);
                        setErrorMsg("");
                      }}
                      className="mt-1"
                    />
                    <span className="text-gray-800">{label}</span>
                  </label>
                ))}
              </fieldset>

              {selectedReason.startsWith("Autre") && (
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Précision du motif</label>
                  <textarea
                    className="w-full border rounded-md p-3 focus:outline-none focus:ring"
                    rows={4}
                    placeholder="Expliquez brièvement la raison du refus…"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                </div>
              )}

              {errorMsg && <p className="text-red-600 text-sm">{errorMsg}</p>}
            </div>

            <div className="px-5 py-4 border-t flex gap-3 justify-end">
              <button
                onClick={closeRefuseModal}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmRefuse}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
                disabled={submitting}
              >
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
